/**
 * TemplatesPage Component
 * Main page for message templates management
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Search, Plus, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  TemplateCardMemo,
  TemplateTableMemo,
  TemplateFiltersMemo,
  TemplateViewToggleMemo,
  TemplateEditorDialogMemo,
} from '@/components/templates';
import { useMessageTemplates } from '@/hooks/useMessageTemplates';
import { TEMPLATE_TABS, TEMPLATE_STORAGE_KEYS } from '@/constants/templateConstants';
import {
  DEFAULT_TEMPLATE_FILTERS,
  type IMessageTemplate,
  type ITemplateFilters,
  type IMessageTemplateCreate,
  type IMessageTemplateUpdate,
} from '@/@types/messageTemplates';
import type { TemplateTab, TemplateViewMode } from '@/constants/templateConstants';
import type { Id } from '../../convex/_generated/dataModel';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';

function TemplatesPage() {
  // Get current business context
  const currentBusiness = useQuery(api.businesses.queries.getCurrentBusiness);
  const businessId = currentBusiness?._id;

  // View mode from localStorage
  const [viewMode, setViewMode] = useState<TemplateViewMode>(() => {
    const saved = localStorage.getItem(TEMPLATE_STORAGE_KEYS.VIEW_PREFERENCE);
    return (saved as TemplateViewMode) || 'grid';
  });

  // Filter state
  const [filters, setFilters] = useState<ITemplateFilters>(DEFAULT_TEMPLATE_FILTERS);

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<IMessageTemplate | undefined>();

  // Custom hook for template operations
  const {
    filteredTemplates,
    userBusinesses,
    isLoading,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    copyTemplate,
  } = useMessageTemplates({ businessId, filters });

  // Save view mode preference
  useEffect(() => {
    localStorage.setItem(TEMPLATE_STORAGE_KEYS.VIEW_PREFERENCE, viewMode);
  }, [viewMode]);

  // Determine if viewing owned templates
  const isOwnerView = filters.tab === 'my_templates' || filters.tab === 'shared_by_me';

  // Handlers
  const handleTabChange = (value: string) => {
    setFilters(prev => ({ ...prev, tab: value as TemplateTab }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, search: e.target.value }));
  };

  const handleNewTemplate = () => {
    setEditingTemplate(undefined);
    setEditorOpen(true);
  };

  const handleEditTemplate = (template: IMessageTemplate) => {
    setEditingTemplate(template);
    setEditorOpen(true);
  };

  const handleDeleteTemplate = useCallback(
    async (templateId: Id<'messageTemplates'>) => {
      try {
        await deleteTemplate(templateId);
      } catch (error) {
        console.error('Failed to delete:', error);
      }
    },
    [deleteTemplate]
  );

  const handleCopyTemplate = useCallback(
    async (templateId: Id<'messageTemplates'>) => {
      try {
        await copyTemplate(templateId);
      } catch (error) {
        console.error('Failed to copy:', error);
      }
    },
    [copyTemplate]
  );

  const handleSaveTemplate = useCallback(
    async (data: IMessageTemplateCreate | IMessageTemplateUpdate) => {
      if ('templateId' in data) {
        await updateTemplate(data);
      } else {
        await createTemplate(data);
      }
    },
    [createTemplate, updateTemplate]
  );

  // Loading state - query still in progress
  if (currentBusiness === undefined) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  // No business found - user needs to set up their business profile first
  if (currentBusiness === null || !businessId) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center px-4">
        <div className="max-w-md">
          <h2 className="text-xl font-semibold text-foreground mb-2">Business Profile Required</h2>
          <p className="text-muted-foreground mb-6">
            To use message templates, you need to set up your business profile first. This helps
            personalize your templates with your business information.
          </p>
          <Button onClick={() => (window.location.href = '/business-settings')} size="lg">
            Go to Business Settings
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Message Templates</h1>
            <p className="text-sm text-muted-foreground">
              Create and manage your messaging templates across all channels
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search templates..."
                value={filters.search}
                onChange={handleSearchChange}
                className="pl-10 w-[250px] bg-background-input"
              />
            </div>

            {/* Import (disabled) */}
            <Button variant="outline" disabled>
              <Download className="h-4 w-4 mr-2" />
              Import
            </Button>

            {/* New Template */}
            <Button onClick={handleNewTemplate}>
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={filters.tab} onValueChange={handleTabChange}>
          <TabsList className="bg-transparent border-b border-border rounded-none h-auto p-0 gap-6">
            {TEMPLATE_TABS.map(tab => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-primary rounded-none px-0 pb-3 text-muted-foreground data-[state=active]:text-foreground"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Filters & View Toggle */}
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <TemplateFiltersMemo
          filters={filters}
          onFiltersChange={setFilters}
          templateCount={filteredTemplates.length}
        />
        <TemplateViewToggleMemo viewMode={viewMode} onViewModeChange={setViewMode} />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-muted-foreground">Loading templates...</p>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <p className="text-muted-foreground mb-4">
              {filters.search
                ? 'No templates match your search'
                : filters.tab === 'my_templates'
                  ? "You haven't created any templates yet"
                  : filters.tab === 'shared_with_me'
                    ? 'No templates have been shared with you'
                    : "You haven't shared any templates"}
            </p>
            {filters.tab === 'my_templates' && !filters.search && (
              <Button onClick={handleNewTemplate}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Template
              </Button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map(template => (
              <TemplateCardMemo
                key={template._id}
                template={template}
                isOwner={isOwnerView}
                onEdit={handleEditTemplate}
                onDelete={handleDeleteTemplate}
                onCopy={handleCopyTemplate}
              />
            ))}
          </div>
        ) : (
          <TemplateTableMemo
            templates={filteredTemplates}
            isOwner={isOwnerView}
            onEdit={handleEditTemplate}
            onDelete={handleDeleteTemplate}
            onCopy={handleCopyTemplate}
          />
        )}
      </div>

      {/* Editor Dialog */}
      <TemplateEditorDialogMemo
        key={editingTemplate?._id ?? 'new'}
        open={editorOpen}
        onOpenChange={setEditorOpen}
        template={editingTemplate}
        businessId={businessId}
        userBusinesses={userBusinesses}
        onSave={handleSaveTemplate}
        onDelete={isOwnerView ? deleteTemplate : undefined}
      />
    </div>
  );
}

export default TemplatesPage;
